import { useEffect, useState, useMemo } from 'react';

import { useBuildVars, useSiteConfig, useInX4AFrame } from 'mf_core/config';
import {
  useIsAuthenticated,
  useIsImpersonationMode,
  useDecodedToken,
} from 'mf_core/auth';
import { useFeatureFlag } from 'mf_core/feature-flag';
import { useLocalizationContext } from '@im/react-localization';

export function useWhatfix(isFirstLogin: boolean) {
  const isAuthenticated = useIsAuthenticated();
  const { buildEnvironment } = useBuildVars();
  const { defaultLocale, siteCode } = useSiteConfig();
  const { locale } = useLocalizationContext();
  const isImpersonationMode = useIsImpersonationMode();
  const { isInX4AFrame } = useInX4AFrame();

  const [isWhatfixUserActive, setIsWhatfixUserActive] = useState(false);
  const decodedToken = useDecodedToken();
  const { value: isWhatfixEnabled } = useFeatureFlag(
    'im-platformcore-whatfix-isenabled'
  );

  const tokenData = useMemo(() => {
    const firstName: string = decodedToken?.imoProfile?.firstName || '';
    const lastName: string = decodedToken?.imoProfile?.lastName || '';
    return {
      email: decodedToken?.imoProfile?.emailAddress || '',
      language: defaultLocale,
      name: `${firstName}${
        firstName && lastName ? ` ${lastName}` : `${lastName}`
      }`,
      resellerId: decodedToken?.resellerId || '',
      userRoles: decodedToken?.role,
      uuid: decodedToken?.imoProfile?.userId,
    };
  }, [decodedToken, defaultLocale]);

  useEffect(() => {
    if (isAuthenticated && tokenData && !isImpersonationMode && !isInX4AFrame) {
      if (!window.whatfixUser) {
        //Initialize whatfix globals
        window.whatfixUser = {
          ...tokenData,
          // uuid: window.btoa(tokenData.uuid),
          uuid: window.btoa(String(tokenData?.uuid)),
        };
        setIsWhatfixUserActive(true);
      }
    }
  }, [
    defaultLocale,
    isAuthenticated,
    isImpersonationMode,
    isInX4AFrame,
    tokenData,
  ]);

  useEffect(() => {
    // Inject whatfix Script in HTML Head

    if (!isFirstLogin && isWhatfixUserActive && isWhatfixEnabled === 'true') {
      const whatfix = document.createElement('script');
      whatfix.type = 'text/javascript';
      whatfix.async = true;
      whatfix.src = `https://cdn.whatfix.com/prod/00f72a09-c312-47b3-b2be-d0afe4a9c9a3/initiator/initiator.nocache.js`;
      const s = document.getElementsByTagName('script')[0];
      if (s?.parentNode) {
        s.parentNode.insertBefore(whatfix, s);
      }
    }
  }, [
    buildEnvironment,
    isAuthenticated,
    isFirstLogin,
    isImpersonationMode,
    isInX4AFrame,
    isWhatfixUserActive,
    siteCode,
    isWhatfixEnabled,
  ]);
  useEffect(() => {
    if (isWhatfixUserActive && locale && window.whatfixUser) {
      //Update global language variable when locale changes
      if (window.whatfixUser.language !== locale)
        window.whatfixUser.language = locale;
    }
  }, [isWhatfixUserActive, locale]);
}

declare global {
  interface Window {
    whatfixUser?: {
      email: string;
      language: string;
      name: string;
      resellerId: string;
      userRoles: string[];
      uuid: string;
    };
  }
}
